import { PageBandScopeProvider } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useHistoryNav } from '@/hooks/use-history-nav';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

/**
 * The chrome both signed-in shells share: a collapsible rail, a 56px header
 * and one scrolling content column.
 *
 * Main (the project console) and Admin differ only in what goes *inside* the
 * rail and the header, so neither shell re-implements the frame. Two
 * hand-rolled frames used to drift apart — different rail padding, different
 * header height, one with a breakpoint hook and one without — which made
 * crossing into /admin read like leaving the product.
 *
 * The rail is the shadcn `Sidebar` primitive, so it collapses to an icon strip,
 * keeps every row's name in a tooltip, and turns into a sheet below `md`
 * without any per-page work.
 */
export function AppShell({
  sidebar,
  header,
  headerAfter,
  bandScope,
}: {
  /** Rail content, ending in the account menu. */
  sidebar: ReactNode;
  /** Header content, laid out after the rail toggle and its separator. */
  header?: ReactNode;
  /** Header content pushed to the far right, after a separator. */
  headerAfter?: ReactNode;
  /**
   * Rendered at the right end of every page band in this shell, before the
   * page's own action. The project console passes its scope selector here for
   * project sub-pages (`MainLayout.bandScopeFor`); the list pages put theirs in
   * the filter row instead. The admin shell passes nothing.
   */
  bandScope?: ReactNode;
}) {
  return (
    <SidebarProvider
      // Read once, at mount: the provider owns the state from then on and
      // writes it back to the same cookie on every toggle.
      defaultOpen={initialSidebarOpen()}
    >
      <Sidebar collapsible="icon">
        {sidebar}
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="flex h-svh flex-col bg-bg">
        {/* 64px tall, `p-4`: the header metrics of the shell this console is
            built to match, with the outlined trigger the reference uses.
            No bottom rule on purpose: the bar is meant to read as part of the
            page rather than as a separate band across the top of it. */}
        <header className="flex h-(--header-height) shrink-0 items-center gap-3 bg-surface p-4 sm:gap-4">
          <SidebarTrigger variant="outline" />
          <Separator orientation="vertical" className="h-6" />
          <HistoryNav />
          {header}
          {headerAfter && (
            <div className="ml-auto flex shrink-0 items-center gap-2">{headerAfter}</div>
          )}
        </header>

        <main className="custom-scroll min-w-0 flex-1 overflow-y-auto">
          {/* One content column, centred and capped, so the page band, the
              toolbar and the panels share a left edge at every width. */}
          <div className="mx-auto w-full max-w-(--page-max)">
            <PageBandScopeProvider scope={bandScope}>
              <Outlet />
            </PageBandScopeProvider>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * Back and forward, where a browser puts them.
 *
 * They are the way out of a *record* — a case or a device — which is a page that
 * belongs to the project it belongs to: switching projects there could not
 * re-scope anything, it would only throw the page away (spec §26). So the band
 * carries no selector on those pages, and the way back is here, next to the
 * breadcrumb that already says where the page came from.
 */
function HistoryNav() {
  const { t } = useTranslation();
  const { canGoBack, canGoForward, goBack, goForward } = useHistoryNav();

  return (
    // Below `sm` the header is the rail trigger and the search field; two more
    // buttons would cost the search its width, and the browser's own chrome is
    // a thumb away there.
    <div className="hidden items-center gap-1 sm:flex">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={goBack}
        disabled={!canGoBack}
        aria-label={t('common.back', 'Back')}
        title={t('common.back', 'Back')}
      >
        <ChevronLeft aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={goForward}
        disabled={!canGoForward}
        aria-label={t('common.forward', 'Forward')}
        title={t('common.forward', 'Forward')}
      >
        <ChevronRight aria-hidden />
      </Button>
    </div>
  );
}

/**
 * The rail's width as the reader left it.
 *
 * `SidebarProvider` has always written this cookie; nothing read it back, so
 * the rail opened on every mount of the shell — including a remount it did not
 * cause, such as crossing between the two route trees (`/docs` and the project
 * panels live in the second one). Collapse the rail, click Docs, and the page
 * opened *and* the rail expanded (spec §32). Reading the cookie seeds the
 * provider the way the reference's server does, which also means the rail now
 * survives a reload.
 */
function initialSidebarOpen(): boolean {
  const m = document.cookie.match(/(?:^|;\s*)sidebar_state=(true|false)/);
  return m ? m[1] === 'true' : true;
}
